import React, { useState, useEffect, useMemo } from 'react';
import { Dropdown } from '../../../components/Dropdown'; 
import { Button } from '../../../components/Button';     
import indiaData from '../../../data/stateData.json';    
import { apiGet } from '../../../services/apiServices';
import { endPoints } from '../../../configs/apiUrls';

const initialFilterState = {
  state: "",
  city: "",
  area: "",
  street: "",
  fromDate: "",
  toDate: "",
};

const ValuarProperiesFilterForm = ({ tableRef, filterData, setFilterData }: any) => {
  const [filters, setFilters] = useState(initialFilterState);
  
  // States for dynamic dropdowns
  const [areaOptions, setAreaOptions] = useState<any[]>([]);
  const [streetOptions, setStreetOptions] = useState<any[]>([]);

  // Sync local filter state with table filter state when opened
  useEffect(() => {
    setFilters({
      state: filterData.state || "",
      city: filterData.city || "",
      area: filterData.area || "",
      street: filterData.street || "",
      fromDate: filterData.fromDate || "",
      toDate: filterData.toDate || "",
    });

    // If city/area already exist in filterData (e.g. reopening drawer), pre-fetch options
    if (filterData.city) fetchAreas(filterData.city);
    if (filterData.area) fetchStreets(filterData.area);
  }, [filterData]);

  // --- API Fetchers ---
  const fetchAreas = async (cityName: string) => {
    try {
      const res: any = await apiGet(`${endPoints.GET_AREA_LIST_BY_CITY}/${cityName}`);
      if (res?.success) {
        const formatted = res.data.map((item: any) => ({
          label: item.label || item.area || item.areaName,
          value: item.value || item._id
        }));
        setAreaOptions(formatted);
      }
    } catch (error) {
      console.error("Failed to fetch areas", error);
    }
  };

  const fetchStreets = async (areaId: string) => {
    try {
      const res: any = await apiGet(`${endPoints.GET_STREET_BY_AREA}/${areaId}`);
      if (res?.success) {
        const formatted = res.data.map((item: any) => ({
          label: item.label || item.street || item.streetName,
          value: item.value || item._id
        }));
        setStreetOptions(formatted);
      }
    } catch (error) {
      console.error("Failed to fetch streets", error);
    }
  };

  // --- Change Handlers ---
  const onFilterChange = (field: string, value: string | null) => {
    if (field === "state") {
      setFilters((prev) => ({ ...prev, state: value || "", city: "", area: "", street: "" }));
      setAreaOptions([]);
      setStreetOptions([]);
      return;
    }
    
    if (field === "city") {
      setFilters((prev) => ({ ...prev, city: value || "", area: "", street: "" }));
      setStreetOptions([]);
      if (value) fetchAreas(value);
      else setAreaOptions([]);
      return;
    }

    if (field === "area") {
      setFilters((prev) => ({ ...prev, area: value || "", street: "" }));
      if (value) fetchStreets(value);
      else setStreetOptions([]);
      return;
    }

    setFilters((prev) => ({ ...prev, [field]: value || "" }));
  };

  // --- Static Options ---
  const stateOptions = useMemo(() => {
    return indiaData.map((s: any) => ({ label: s.state, value: s.state }));
  }, []);

  const cityOptions = useMemo(() => {
    if (!filters.state) return [];
    const stateObj = indiaData.find((s: any) => s.state === filters.state);
    if (!stateObj) return [];
    return stateObj.districts.map((d: string) => ({ label: d, value: d }));
  }, [filters.state]);

  // --- Action Buttons ---
  const handleFilter = () => {
    setFilterData((prev: any) => ({
      ...prev,
      ...filters,
      page: 1, // Reset to page 1 on filter
    }));
    if (tableRef?.current) {
      tableRef.current.closeFilterDrawer();
    }
  };

  const handleClear = () => {
    setFilterData((prev: any) => ({
      ...prev,
      ...initialFilterState,
      page: 1,
    }));
    setFilters(initialFilterState);
    setAreaOptions([]);
    setStreetOptions([]);
    
    if (tableRef?.current) {
      tableRef.current.closeFilterDrawer();
    }
  };

  return (
    <div className="h-full flex flex-col p-4">
      <div className="flex-grow overflow-auto pb-6">
        <div className="flex flex-col gap-4">
          
          {/* Location Filters */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
            <Dropdown
              options={stateOptions}
              value={filters.state}
              onChange={(val: string | null) => onFilterChange("state", val)}
              placeholder="Select state"
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <Dropdown
              options={cityOptions}
              value={filters.city}
              onChange={(val: string | null) => onFilterChange("city", val)}
              placeholder={!filters.state ? "Select State First" : "Select city"}
              disabled={!filters.state}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Area</label>
            <Dropdown
              options={areaOptions}
              value={filters.area}
              onChange={(val: string | null) => onFilterChange("area", val)}
              placeholder={!filters.city ? "Select City First" : "Select area"}
              disabled={!filters.city}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Street</label>
            <Dropdown
              options={streetOptions}
              value={filters.street}
              onChange={(val: string | null) => onFilterChange("street", val)}
              placeholder={!filters.area ? "Select Area First" : "Select street"}
              disabled={!filters.area}
              className="w-full"
            />
          </div>

          <hr className="my-2 border-gray-200" />

          {/* Date Range Filters */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => onFilterChange("fromDate", e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 shadow-sm text-sm text-gray-700 focus:ring-2 focus:ring-blue-600 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={filters.toDate}
              min={filters.fromDate} // Prevents selecting a 'To' date earlier than 'From'
              onChange={(e) => onFilterChange("toDate", e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 shadow-sm text-sm text-gray-700 focus:ring-2 focus:ring-blue-600 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400"
              disabled={!filters.fromDate}
            />
          </div>

        </div>
      </div>
      
      {/* Fixed footer buttons */}
      <div className="border-t pt-4 flex justify-end gap-3 bg-white flex-shrink-0">
        <Button onClick={handleClear} variant="outline">
          Clear
        </Button>
        <Button onClick={handleFilter}>Apply Filters</Button>
      </div>
    </div>
  );
};

export default ValuarProperiesFilterForm;